import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Input, InputNumber, Select, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import { createTdsSurchargeCess, updateTdsSurchargeCess, fetchTdsSurchargeCessList } from "../../../../core/data/redux/payroll/TDS/tdsSurchargeCess/tdsSurchargeCessActions";
import { fetchTdsTaxRegimes } from "../../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";
import { fetchFinancialYearsByCompanyId } from "../../../../core/data/redux/financialYear/financialYearActions";

const SurchargeCessModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loading } = useSelector((state: any) => state.TDS.tdsSurchargeCess);
    const { regimes } = useSelector((state: any) => state.TDS.tdsTaxRegime);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    const { financialYearsByCompany } = useSelector((state: any) => state.financialYears);
    const companyId = activeCompany?.company_detail_id

    const [formData, setFormData] = useState({
        FinancialYearID: null as number | null,
        IncomeThresholdForSurcharge1: 0,
        SurchargeRate1_Percentage: 0,
        IncomeThresholdForSurcharge2: 0,
        SurchargeRate2_Percentage: 0,
        HealthAndEducationCessRate_Percentage: 0,
        Status: "active",
        TaxRegimeID: null as number | null,
    });

    const [errors, setErrors] = useState<any>({});

    const resetForm = useCallback(() => {
        setFormData({
            FinancialYearID: null as number | null,
            IncomeThresholdForSurcharge1: 0,
            SurchargeRate1_Percentage: 0,
            IncomeThresholdForSurcharge2: 0,
            SurchargeRate2_Percentage: 0,
            HealthAndEducationCessRate_Percentage: 0,
            Status: "active",
            TaxRegimeID: null,
        });
        setErrors({});
    }, []);

    useEffect(() => {
        if (companyId) {
            dispatch(fetchFinancialYearsByCompanyId(companyId))
        }
    }, [dispatch, companyId])

    useEffect(() => {
        dispatch(fetchTdsTaxRegimes())
    }, [dispatch])

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                FinancialYearID: selectedRowData.FinancialYearID != null ? Number(selectedRowData.FinancialYearID) : null,
                IncomeThresholdForSurcharge1: selectedRowData.IncomeThresholdForSurcharge1 || 0,
                SurchargeRate1_Percentage: selectedRowData.SurchargeRate1_Percentage || 0,
                IncomeThresholdForSurcharge2: selectedRowData.IncomeThresholdForSurcharge2 || 0,
                SurchargeRate2_Percentage: selectedRowData.SurchargeRate2_Percentage || 0,
                HealthAndEducationCessRate_Percentage: selectedRowData.HealthAndEducationCessRate_Percentage || 0,
                Status: selectedRowData.Status || "active",
                TaxRegimeID: selectedRowData.TaxRegimeID ?? null,
            });
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData, resetForm]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        const newErrors: any = {};
        let valid = true;

        if (formData.FinancialYearID == null) {
            newErrors.FinancialYearID = "Financial Year is required";
            valid = false;
        }
        if (formData.TaxRegimeID == null) {
            newErrors.TaxRegimeID = "Tax Regime is required";
            valid = false;
        }

        if (
            formData.HealthAndEducationCessRate_Percentage === null ||
            formData.HealthAndEducationCessRate_Percentage === undefined

        ) {
            newErrors.HealthAndEducationCessRate_Percentage = "Health & Education Cess Rate is required";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const payload = {
            ...formData,
            TaxRegimeID: formData.TaxRegimeID ?? null,
            ...(modalType === "edit"
                ? { ModifiedBy: user?.UserId || 1 }
                : { CreatedBy: user?.UserId || 1 }),
        };

        try {
            if (modalType === "edit") {
                await dispatch(
                    updateTdsSurchargeCess({ id: selectedRowData?.SurchargeCessID, ...payload })
                ).unwrap();
                message.success("Record updated successfully");
            } else {
                await dispatch(createTdsSurchargeCess(payload)).unwrap();
                message.success("Record created successfully");
            }
            handleCancel();
            resetForm();
            await dispatch(fetchTdsSurchargeCessList());
        } catch {
            message.error("Something went wrong");
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Surcharge & Cess" : "Add Surcharge & Cess"}
            placement="right"
            width={520}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <div className="mb-3">
                <label className="form-label">
                    Financial Year <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                    value={formData.FinancialYearID}
                    onChange={(val) => handleChange("FinancialYearID", val)}
                    className="w-100"
                    placeholder="Select financial year"
                    allowClear
                    options={
                        (financialYearsByCompany || []).map((fy: any) => ({
                            value: fy.id,
                            label: fy.name,
                        }))
                    }
                />
                {errors.FinancialYearID && (
                    <div className="text-danger">{errors.FinancialYearID}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label">
                    Tax Regime <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                    value={formData.TaxRegimeID}
                    onChange={(val) => handleChange("TaxRegimeID", val)}
                    className="w-100"
                    placeholder="Select tax regime"
                    allowClear
                    options={
                        (regimes || [])
                            .filter((r: any) => r.Status === "active")
                            .map((r: any) => ({
                                value: r.TaxRegimeID,
                                label: `${r.RegimeName} (FY ${r.FinancialYearID})`,
                            }))
                    }
                />
                {errors.TaxRegimeID && <div className="text-danger">{errors.TaxRegimeID}</div>}
            </div>


            {[
                ["IncomeThresholdForSurcharge1", "Income Threshold for Surcharge 1"],
                ["SurchargeRate1_Percentage", "Surcharge Rate 1 (%)"],
                ["IncomeThresholdForSurcharge2", "Income Threshold for Surcharge 2"],
                ["SurchargeRate2_Percentage", "Surcharge Rate 2 (%)"],
                ["HealthAndEducationCessRate_Percentage", "Health & Edu. Cess Rate (%)"],
            ].map(([key, label]) => (
                <div className="mb-3" key={key}>
                    <label className="form-label">{label}</label>
                    <InputNumber
                        min={0}
                        value={(formData as any)[key]}
                        onChange={(value) => handleChange(key, value)}
                        style={{ width: "100%" }}
                    />
                    {errors[key] && <div className="text-danger">{errors[key]}</div>}
                </div>
            ))}

            <div className="mb-3">
                <label className="form-label">Status</label>
                <Select
                    value={formData.Status}
                    onChange={(val) => handleChange("Status", val)}
                    className="w-100"
                >
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="inactive">Inactive</Select.Option>
                </Select>
            </div>

            <div className="modal-footer mt-4">
                <button className="btn btn-secondary me-2" onClick={handleCancel}>
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading && <span className="spinner-border spinner-border-sm me-2" />}
                    {modalType === "edit" ? "Update" : "Create"}
                </button>
            </div>
        </Drawer>
    );
};

export default SurchargeCessModal;
